/**
 * GF Spam Hexer — Frontend Entry Point
 *
 * Exposes the collector initializer as a global function so that GF's
 * init scripts (registered via `GFFormDisplay::add_init_script()`) can
 * call it per-form at the right lifecycle moment (`ON_PAGE_RENDER`).
 *
 * The PHP side (`GF_Spam_Hexer\Frontend\Frontend::add_init_scripts()`) injects:
 *   window.gfshInitCollector({ formId, powEnabled, ... });
 *
 * It also publishes the same config on `window.gfshFormConfigs`, which
 * `selfInitPublishedForms()` below reads on DOM ready.
 */

import { initCollector } from '../collector';
import { createLogger } from '@shared/logger';

const logger = createLogger('Collector');

/**
 * Shape of the per-form config passed from PHP via init scripts.
 */
export interface GpshFrontendConfig {
	formId: number;
	powEnabled: boolean;
	workerUrl: string;
	payloadField: string;
	powOnSubmit?: boolean;
	hasPages?: boolean;
	powChallenge?: {
		challenge: string;
		signature: string;
		difficulty: number;
		expires: number;
	};
}

// Expose the collector initializer globally for GF init scripts.
(window as unknown as Record<string, unknown>).gfshInitCollector =
	initCollector;

/**
 * Initializes the collector for every form PHP published a config for.
 *
 * `gform_post_render` is the only thing that calls `window.gfshInitCollector`,
 * and GF does not reliably fire it: `GFFormDisplay::post_render_script()` finds
 * the wrapper with `getElementById( 'gform_wrapper_<id>' )` and parks on a
 * MutationObserver when that first match is hidden. Themes that ship separate
 * desktop and mobile markup, rather than restyling one form, produce exactly
 * that: nothing runs, and every submission is flagged `pow_missing`.
 *
 * `initCollector()` treats the later `gform_post_render` call as a repeat, so
 * this does not cost a second challenge request.
 */
function selfInitPublishedForms(): void {
	const configs = (
		window as unknown as {
			gfshFormConfigs?: Record<string, GpshFrontendConfig>;
		}
	).gfshFormConfigs;

	if (!configs) {
		return;
	}

	for (const config of Object.values(configs)) {
		if (!config?.formId) {
			continue;
		}

		// Only forms actually on the page.
		if (!document.querySelector(`form[id="gform_${config.formId}"]`)) {
			logger.log(
				`formId ${config.formId} — published config has no form in the DOM, skipping self-init`
			);
			continue;
		}

		initCollector(config);
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', selfInitPublishedForms);
} else {
	selfInitPublishedForms();
}
